import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { message } from 'antd';
import axios from 'axios';
import {
    PlusOutlined,
    SearchOutlined,
    FolderAddOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import useMediaQuery from '@/hooks/useMediaQuery/useMediaQuery';

import ProjectSidebar from './ProjectSidebar';
import DashboardFilters from './DashboardFilters';
import PipelineFunnelCards from './PipelineFunnelCards';
import WorkloadChart from './WorkloadChart';
import AIIntelligenceCenter from './AIIntelligenceCenter';
import TaskViewTabs from './TaskViewTabs';
import TaskListTable from './TaskListTable';
import CreateProjectModal from './modals/CreateProjectModal';
import TaskDetailDrawer from './TaskDrawer';
import KanbanBoard from './kanban';
import TimelineBoard from './timeline';
import SprintBoard from './sprint';
import MilestoneCardView from './MilestoneCardView';
import ProjectDashboard from '../ProjectDashboard';
import ProjectFiles from './files';
import ProjectInbox from './inbox';
import { getInboxFeed } from './inbox/api/inboxApi';

import { selectCurrentAdmin } from '@/redux/auth/selectors';

import {
    fetchTasks,
    fetchTaskSummary,
    fetchProjects,
    deleteTask,
    updateTaskStatus,
    convertWorkItemType,
    fetchTaskById,
} from './api/projectManagementApi';

import MilestoneCompletionModal from './TaskDrawer/components/MilestoneCompletionModal';

/**
 * ProjectManagementDashboard
 * Sidebar-driven navigation: Overview (dashboard), Milestones, plus future-phase stubs.
 */
export default function ProjectManagementDashboard({ initialNav }) {
    // ── Data state ──────────────────────────────────────────────────────────
    const [tasks, setTasks] = useState([]);
    const [taskSummary, setTaskSummary] = useState({});
    const [projects, setProjects] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [activeProject, setActiveProject] = useState(null);
    const [loading, setLoading] = useState(false);

    const currentAdmin = useSelector(selectCurrentAdmin);
    const location = useLocation();
    const history = useHistory();
    const isMobile = useMediaQuery('(max-width: 1024px)');

    // ── Sidebar open/close ────────────────────────────────────────────────────
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [userToggledSidebar, setUserToggledSidebar] = useState(false);

    // ── Active Section ───────────────────────────────────────────────────────
    const [activeNav, setActiveNav] = useState(() => {
        if (initialNav) return initialNav;
        if (location.state?.activeNav) return location.state.activeNav;
        if (location.pathname.includes('project-dashboard')) return 'dashboard';
        return 'overview';
    }); // sidebar nav key

    const [inboxBadgeCount, setInboxBadgeCount] = useState(0);

    // Synchronize live inbox badge count dynamically
    useEffect(() => {
        let isMounted = true;
        const loadInboxBadge = async () => {
            try {
                const res = await getInboxFeed({ tab: 'all', limit: 1 });
                if (isMounted && res?.data?.counts) {
                    const count = res.data.counts.action || res.data.counts.unread || 0;
                    setInboxBadgeCount(count);
                }
            } catch (err) {
                // graceful fallback
            }
        };
        loadInboxBadge();
        return () => { isMounted = false; };
    }, [activeNav]);

    useEffect(() => {
        if (location.pathname.includes('project-dashboard')) {
            setActiveNav('dashboard');
        } else if (location.pathname.includes('project-management') && activeNav === 'dashboard') {
            setActiveNav('overview');
        }
    }, [location.pathname]);

    useEffect(() => {
        if (!userToggledSidebar) {
            setSidebarOpen(!isMobile);
        }
    }, [isMobile, userToggledSidebar]);

    const handleSidebarToggle = () => {
        setUserToggledSidebar(true);
        setSidebarOpen((prev) => !prev);
    };

    // ── UI state ─────────────────────────────────────────────────────────────
    const [activeTaskTab, setActiveTaskTab] = useState('list');  // list/kanban/timeline/sprint
    const [visitedTabs, setVisitedTabs] = useState(() => new Set(['list']));
    const [statusFilter, setStatusFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [listPage, setListPage] = useState(1);
    const LIST_PAGE_SIZE = 10;

    useEffect(() => {
        setVisitedTabs((prev) => {
            if (prev.has(activeTaskTab)) return prev;
            const next = new Set(prev);
            next.add(activeTaskTab);
            return next;
        });
    }, [activeTaskTab]);

    const [filters, setFilters] = useState({
        dateRange: 'All Time',
        owner: '',
        projectId: '',
    });

    useEffect(() => {
        setListPage(1);
    }, [filters, statusFilter, searchQuery]);

    // ── Drawer / modal state ─────────────────────────────────────────────────
    const [drawerMode, setDrawerMode] = useState('view');
    const [drawerTaskId, setDrawerTaskId] = useState(null);
    const [drawerDefaultStatus, setDrawerDefaultStatus] = useState('In Progress');
    const [drawerSelectedProject, setDrawerSelectedProject] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
    const [projectToEdit, setProjectToEdit] = useState(null);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // ── Milestone Completion Modal state ─────────────────────────────────────
    const [completingMilestone, setCompletingMilestone] = useState(null);
    const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
    const [completionLoading, setCompletionLoading] = useState(false);

    // ── Deep-link from notification click ────────────────────────────────────
    const [pendingAlertId, setPendingAlertId] = useState(null);

    useEffect(() => {
        // Deep-link from query param ?task=TSK-... or ?taskId=...
        const searchParams = new URLSearchParams(location.search);
        const queryTaskId = searchParams.get('task') || searchParams.get('taskId');
        if (queryTaskId) {
            const t = setTimeout(() => openTaskDrawer(queryTaskId), 300);
            return () => clearTimeout(t);
        }

        const state = location.state;
        if (!state) return;

        if (state.taskId) {
            const t = setTimeout(() => openTaskDrawer(state.taskId), 300);
            history.replace(location.pathname);
            return () => clearTimeout(t);
        }

        if (state.alertId) {
            setActiveNav('overview');
            setPendingAlertId(state.alertId);
        }

        if (state.projectId) {
            setActiveNav('overview');
            setFilters(prev => ({ ...prev, projectId: state.projectId }));
        }

        if (state.activeNav) {
            setActiveNav(state.activeNav);
        }

        history.replace(location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // ── Data fetching ────────────────────────────────────────────────────────
    const loadEmployees = useCallback(async () => {
        try {
            const { data } = await axios.get('/employeesNames');
            const list = (data?.names || []).map((e) => ({
                _id: e.id,
                name: e.fullName || `${e.name || ''} ${e.surname || ''}`.trim(),
            }));
            setEmployees(list);
        } catch { /* non-critical */ }
    }, []);

    const loadProjects = useCallback(async () => {
        try {
            const { data } = await fetchProjects();
            const list = data?.result || data?.projects || [];
            setProjects(list);
        } catch { /* non-critical */ }
    }, []);

    const loadTaskSummary = useCallback(async () => {
        try {
            const params = {};
            if (filters.projectId) params.projectId = filters.projectId;
            const { data } = await fetchTaskSummary(params);
            setTaskSummary(data?.result || {});
        } catch { setTaskSummary({}); }
    }, [filters.projectId]);

    const loadTasks = useCallback(async (page = 1, limitOverride = 'all') => {
        setLoading(true);
        try {
            const params = { page, limit: limitOverride };
            if (filters.projectId) params.projectId = filters.projectId;
            if (filters.owner) params.owner = filters.owner;
            if (filters.dateRange) params.dateRange = filters.dateRange;
            if (statusFilter) params.status = statusFilter;
            const { data } = await fetchTasks(params);
            setTasks(data?.result || []);
        } catch {
            message.error('Failed to load tasks.');
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [filters.projectId, filters.owner, filters.dateRange, statusFilter]);

    // Parallel startup fetch
    useEffect(() => {
        Promise.all([
            loadEmployees(),
            loadProjects(),
            loadTasks(1, 'all'),
            loadTaskSummary(),
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-fetch on filter/status change
    useEffect(() => {
        Promise.all([loadTasks(1, 'all'), loadTaskSummary()]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, statusFilter, activeNav]);

    // Socket — live effort and task updates
    useEffect(() => {
        if (!currentAdmin?._id) return;
        const socket = connectSocket({ userId: currentAdmin._id, role: currentAdmin.role });
        if (!socket) return;

        const handleEffortUpdate = (payload) => {
            const updatedTaskId = String(payload?.taskId || '');
            if (!updatedTaskId) return;
            setTasks((prev) => prev.map((task) =>
                String(task._id) === updatedTaskId
                    ? {
                        ...task,
                        actualTimeMs: typeof payload.actualTimeMs === 'number'
                            ? payload.actualTimeMs
                            : task.actualTimeMs,
                    }
                    : task
            ));
        };

        const handleProjectTaskUpdated = async (payload) => {
            const taskId = String(payload?.taskId || '');
            const changeType = payload?.changeType;
            if (!taskId) return;

            if (changeType === 'deleted') {
                setTasks((prev) => prev.filter((t) => String(t._id) !== taskId));
                loadTaskSummary();
            } else {
                try {
                    const { data } = await fetchTaskById(taskId);
                    const taskObj = data?.result;
                    if (taskObj) {
                        setTasks((prev) => {
                            const exists = prev.some((t) => String(t._id) === taskId);
                            if (exists) {
                                return prev.map((t) => String(t._id) === taskId ? taskObj : t);
                            } else {
                                return [taskObj, ...prev];
                            }
                        });
                        loadTaskSummary();
                    }
                } catch (err) {
                    console.error('[Socket] Failed to fetch task details for taskId:', taskId, err);
                }
            }
        };

        socket.on('projectTask:effortUpdated', handleEffortUpdate);
        socket.on('projectTask:updated', handleProjectTaskUpdated);

        return () => {
            socket.off('projectTask:effortUpdated', handleEffortUpdate);
            socket.off('projectTask:updated', handleProjectTaskUpdated);
            releaseSocket();
        };
    }, [currentAdmin?._id, currentAdmin?.role]);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleFilterChange = (newFilters) => setFilters(newFilters);
    const handleStatusFilterChange = (status) => setStatusFilter(status);
    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setListPage(newPage);
    };

    const handleTaskCreated = (newTask) => {
        setTasks((prev) => [newTask, ...prev]);
        loadTaskSummary();
    };

    const handleTaskUpdated = (updatedTask) => {
        setTasks((prev) => prev.map((t) => (t._id === updatedTask._id ? updatedTask : t)));
        loadTaskSummary();
    };

    const handleProjectCreated = (newProject) => {
        setProjects((prev) => [newProject, ...prev]);
        setActiveProject(newProject);
        loadProjects();
        loadTasks(1, 'all');
        loadTaskSummary();
    };

    const [drawerItemType, setDrawerItemType] = useState('task');

    const openCreateDrawer = (prefillStatus = 'In Progress', prefillItemType = 'task') => {
        setDrawerMode('create');
        setDrawerTaskId(null);
        setDrawerDefaultStatus(prefillStatus || 'In Progress');
        setDrawerItemType(prefillItemType || 'task');
        setDrawerSelectedProject(activeProject);
        setIsDrawerOpen(true);
    };

    const openTaskDrawer = (taskId) => {
        let validTaskId = taskId;
        const isHexId = typeof taskId === 'string' && /^[0-9a-fA-F]{24}$/.test(taskId);
        if (!isHexId && tasks && tasks.length > 0) {
            const matched = tasks.find((t) =>
                (t.taskId && taskId && String(t.taskId).toLowerCase() === String(taskId).toLowerCase()) ||
                (t.title && taskId && String(t.title).toLowerCase().includes(String(taskId).toLowerCase())) ||
                (t._id && taskId && String(t._id) === String(taskId))
            );
            if (matched) {
                validTaskId = matched._id;
            }
        }
        setDrawerMode('view');
        setDrawerTaskId(validTaskId);
        setDrawerDefaultStatus('In Progress');
        setDrawerSelectedProject(null);
        setIsDrawerOpen(true);
    };

    const closeTaskDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerMode('view');
        setDrawerTaskId(null);
        setDrawerDefaultStatus('In Progress');
        setDrawerSelectedProject(null);
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.has('task') || searchParams.has('taskId')) {
            searchParams.delete('task');
            searchParams.delete('taskId');
            const newSearch = searchParams.toString();
            history.replace({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : '',
            });
        }
    };

    const handleTaskCreatedAndOpen = (newTask) => {
        handleTaskCreated(newTask);
        openTaskDrawer(newTask?._id);
    };

    const handleEdit = (task) => openTaskDrawer(task._id);
    const handleRowClick = (task) => openTaskDrawer(task._id);

    const handleDelete = async (taskId) => {
        setDeletingId(taskId);
        try {
            await deleteTask(taskId);
            message.success('Task deleted.');
            setTasks((prev) => prev.filter((t) => t._id !== taskId));
            loadTaskSummary();
        } catch {
            message.error('Failed to delete task.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleConvertType = async (item) => {
        try {
            const { data } = await convertWorkItemType(item._id);
            message.success(data?.message || 'Work item type updated.');
            loadTasks(1, 'all');
            loadTaskSummary();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to convert work item type.');
        }
    };

    const handleNavChange = (navKey, state = {}) => {
        setActiveNav(navKey);
        if (navKey === 'dashboard') {
            history.push('/project-dashboard', state);
        } else {
            history.replace('/project-management', state);
        }
        if (navKey === 'milestones' || navKey === 'overview') {
            setFilters(prev => ({ ...prev, itemType: '' }));
        }
    };

    const handleInboxNavigate = (navKey, options = {}) => {
        handleNavChange(navKey, options);
        if (options?.taskTab) {
            setActiveTaskTab(options.taskTab);
        }
    };

    const handleStatusChange = async (taskId, newStatus, options = {}) => {
        const targetTask = tasks.find(t => String(t._id) === String(taskId));
        if (targetTask && targetTask.itemType === 'milestone' && newStatus === 'Completed' && !options.skipModal) {
            setCompletingMilestone(targetTask);
            setIsCompletionModalOpen(true);
            return;
        }

        setUpdatingStatusId(taskId);
        try {
            const extra = options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {};
            await updateTaskStatus(taskId, newStatus, options.note || '', options.completionComments || '', extra);
            setTasks((prev) => prev.map((t) => (t._id === taskId ? {
                ...t,
                status: newStatus,
                ...(options.deliverableChecklist ? { deliverableChecklist: options.deliverableChecklist } : {})
            } : t)));
            loadTaskSummary();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to update status.');
        } finally {
            setUpdatingStatusId(null);
        }
    };

    const handleConfirmMilestoneCompletion = async (comments, verifiedChecklist) => {
        if (!completingMilestone) return;
        setCompletionLoading(true);
        try {
            await handleStatusChange(completingMilestone._id, 'Completed', {
                skipModal: true,
                completionComments: comments,
                deliverableChecklist: verifiedChecklist,
            });
            setIsCompletionModalOpen(false);
            setCompletingMilestone(null);
            message.success('Milestone marked as completed and synced to CRE!');
        } catch {
            message.error('Failed to complete milestone.');
        } finally {
            setCompletionLoading(false);
        }
    };

    const resolveInitialProjectTab = (project) => {
        if (!project) return 'list';
        const views = project.availableViews || project.customFields?.availableViews || { list: true, kanban: true, timeline: true, sprint: true };
        const sprintEnabled = (project.sprintManagement ?? project.autoSprintPlanning ?? true);

        const isAvailable = (key) => {
            if (key === 'sprint' && !sprintEnabled) return false;
            if (views && views[key] !== undefined) return Boolean(views[key]);
            return true;
        };

        const requestedView = project.defaultView || project.customFields?.defaultView || 'Kanban Board View';
        const lower = String(requestedView).toLowerCase();
        let requestedKey = 'list';
        if (lower.includes('sprint')) requestedKey = 'sprint';
        else if (lower.includes('kanban')) requestedKey = 'kanban';
        else if (lower.includes('timeline')) requestedKey = 'timeline';

        if (isAvailable(requestedKey)) return requestedKey;

        const fallbackOrder = ['list', 'kanban', 'timeline', 'sprint'];
        const firstValid = fallbackOrder.find((k) => isAvailable(k));
        return firstValid || 'list';
    };

    const handleProjectClick = (project) => {
        setActiveProject(project);
        setFilters((prev) => ({ ...prev, projectId: project._id }));
        if (project) {
            const initialTabKey = resolveInitialProjectTab(project);
            setActiveTaskTab(initialTabKey);
        }
    };

    const handleShowAllProjects = () => {
        setActiveProject(null);
        setFilters((prev) => ({ ...prev, projectId: '' }));
    };

    const visibleTasks = useMemo(() => {
        if (!searchQuery.trim()) return tasks;
        const q = searchQuery.toLowerCase();
        return tasks.filter(
            (t) =>
                t.title?.toLowerCase().includes(q) ||
                t.taskId?.toLowerCase().includes(q) ||
                t.assignee?.name?.toLowerCase().includes(q)
        );
    }, [tasks, searchQuery]);

    // Filter milestones out from task boards unless explicitly selected via Work Item Type filter (Section 25)
    const dashboardTasks = useMemo(() => {
        return visibleTasks.filter((t) => {
            if (filters.itemType === 'milestone') return t.itemType === 'milestone';
            if (filters.itemType === 'task') return t.itemType !== 'milestone';
            return t.itemType !== 'milestone';
        });
    }, [visibleTasks, filters.itemType]);

    const paginatedListTasks = useMemo(() => {
        const start = (listPage - 1) * LIST_PAGE_SIZE;
        return dashboardTasks.slice(start, start + LIST_PAGE_SIZE);
    }, [dashboardTasks, listPage]);

    const listPagination = useMemo(() => ({
        page: listPage,
        limit: LIST_PAGE_SIZE,
        total: dashboardTasks.length,
        totalPages: Math.max(1, Math.ceil(dashboardTasks.length / LIST_PAGE_SIZE)),
    }), [listPage, dashboardTasks.length]);

    const isFilesNav = activeNav === 'files';

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className={`bg-[#f8f9fa] dark:bg-[#141414] ${isFilesNav ? 'h-[calc(100vh-56px)] max-h-[calc(100vh-56px)] overflow-hidden' : 'min-h-[calc(100vh-56px)]'}`}>

            {/* ── Left sidebar (fixed) ── */}
            <ProjectSidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                activeNav={activeNav}
                onNavChange={handleNavChange}
                inboxBadgeCount={inboxBadgeCount}
            />

            {/* ── Main content — offset by sidebar width so nothing is hidden ── */}
            <main
                className={`min-w-0 transition-all duration-300 ease-in-out ${
                    isFilesNav
                        ? 'p-3 h-full max-h-full overflow-hidden'
                        : 'px-6 py-8 space-y-6'
                }`}
                style={{ marginLeft: sidebarOpen ? '256px' : '56px' }}
            >

                {/* PROJECT DASHBOARD — embedded project list & KPI table */}
                {activeNav === 'dashboard' && (
                    <ProjectDashboard hideSidebar={true} />
                )}

                {/* OVERVIEW — fully functional project dashboard */}
                {activeNav === 'overview' && (
                    <>
                        {/* Section 1: Header + Filters */}
                        <section className="space-y-4">
                            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                                <div>
                                    <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                        Project Dashboard
                                    </h1>
                                    <p className="text-[#424750] dark:text-slate-400 font-medium">
                                        Enterprise Resource Planning &amp; Performance Tracking
                                    </p>
                                </div>

                                <div className="flex items-center gap-3 flex-wrap">
                                    <button
                                        onClick={() => setIsCreateProjectOpen(true)}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm border border-[#03488a] text-[#03488a] dark:text-[#a7c8ff] dark:border-[#a7c8ff] hover:bg-[#03488a]/5 transition-all"
                                    >
                                        <FolderAddOutlined />
                                        New Project
                                    </button>

                                    <DashboardFilters
                                        filters={filters}
                                        onFilterChange={handleFilterChange}
                                        projects={projects}
                                        employees={employees}
                                        activeProject={activeProject}
                                        onProjectClick={handleProjectClick}
                                        onShowAll={handleShowAllProjects}
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Section 2: Pipeline Cards */}
                        <PipelineFunnelCards
                            summary={taskSummary}
                            tasks={tasks}
                            activeFilter={statusFilter}
                            onFilterByStatus={handleStatusFilterChange}
                        />

                        {/* Section 3: Workload + AI Intelligence Center */}
                        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <WorkloadChart tasks={tasks} />
                            <AIIntelligenceCenter
                                projectId={filters.projectId || null}
                                onTaskClick={(task) => openTaskDrawer(task?._id || task)}
                                pendingAlertId={pendingAlertId}
                                onPendingAlertConsumed={() => setPendingAlertId(null)}
                            />
                        </section>

                        {/* Section 4: Task Views */}
                        <section className="space-y-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                                <TaskViewTabs
                                    activeTab={activeTaskTab}
                                    onTabChange={setActiveTaskTab}
                                    availableViews={activeProject?.availableViews || activeProject?.customFields?.availableViews}
                                    sprintManagement={activeProject?.sprintManagement ?? activeProject?.autoSprintPlanning ?? true}
                                />

                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <SearchOutlined
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                            style={{ fontSize: 16 }}
                                        />
                                        <input
                                            className="bg-white dark:bg-[#1e293b] border-none pl-9 pr-4 py-2 rounded-xl text-sm focus:ring-2 focus:ring-[#28b5fd] w-56 shadow-sm text-[#191c1d] dark:text-white outline-none"
                                            placeholder="Search tasks..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    <button
                                        onClick={() => openCreateDrawer()}
                                        className="text-white flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow-sm hover:opacity-90 active:scale-95 transition-all"
                                        style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                                    >
                                        <PlusOutlined />
                                        New Task
                                    </button>
                                </div>
                            </div>

                            {visitedTabs.has('list') && (
                                <div className={activeTaskTab === 'list' ? 'block' : 'hidden'}>
                                    <TaskListTable
                                        tasks={paginatedListTasks}
                                        loading={loading}
                                        pagination={listPagination}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onStatusChange={handleStatusChange}
                                        onPageChange={handlePageChange}
                                        onRowClick={handleRowClick}
                                        onConvertType={handleConvertType}
                                        onAddSubtask={(task) => openCreateDrawer('In Progress')}
                                        updatingStatusId={updatingStatusId}
                                        deletingId={deletingId}
                                    />
                                </div>
                            )}

                            {visitedTabs.has('kanban') && (
                                <div className={activeTaskTab === 'kanban' ? 'block' : 'hidden'}>
                                    <KanbanBoard
                                        tasks={dashboardTasks}
                                        loading={loading}
                                        onStatusChange={handleStatusChange}
                                        onRowClick={handleRowClick}
                                        onEdit={handleEdit}
                                        onDelete={handleDelete}
                                        onAddTask={(status) => openCreateDrawer(status)}
                                    />
                                </div>
                            )}

                            {visitedTabs.has('timeline') && (
                                <div className={activeTaskTab === 'timeline' ? 'block' : 'hidden'}>
                                    <TimelineBoard
                                        tasks={dashboardTasks}
                                        loading={loading}
                                        onRowClick={handleRowClick}
                                    />
                                </div>
                            )}

                            {visitedTabs.has('sprint') && (
                                <div className={activeTaskTab === 'sprint' ? 'block' : 'hidden'}>
                                    <SprintBoard
                                        tasks={dashboardTasks}
                                        loading={loading}
                                        onStatusChange={handleStatusChange}
                                        onRowClick={handleRowClick}
                                    />
                                </div>
                            )}
                        </section>
                    </>
                )}

                {/* MILESTONES — dedicated Milestones Page */}
                {activeNav === 'milestones' && (
                    <section className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                            <div>
                                <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                    Project Milestones
                                </h1>
                                <p className="text-[#424750] dark:text-slate-400 font-medium">
                                    Track major project stages, deliverables, approvals, and connected phase tasks.
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => openCreateDrawer('In Progress', 'milestone')}
                                    className="text-white flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm shadow-sm hover:opacity-90 active:scale-95 transition-all"
                                    style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                                >
                                    <PlusOutlined />
                                    New Milestone
                                </button>

                                <DashboardFilters
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                    projects={projects}
                                    employees={employees}
                                    activeProject={activeProject}
                                    onProjectClick={handleProjectClick}
                                    onShowAll={handleShowAllProjects}
                                />
                            </div>
                        </div>

                        <MilestoneCardView
                            milestones={tasks.filter((t) => t.itemType === 'milestone')}
                            tasks={tasks}
                            loading={loading}
                            onMilestoneClick={(ms) => openTaskDrawer(ms._id)}
                            onStatusChange={handleStatusChange}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onConvertType={handleConvertType}
                        />
                    </section>
                )}

                {/* PROJECT FILES — Centralized Production File & Project Asset Management */}
                {activeNav === 'files' && (
                    <ProjectFiles />
                )}

                {/* INBOX — Centralized Action & Activity Control Center */}
                {activeNav === 'inbox' && (
                    <ProjectInbox
                        workspaceTasks={tasks}
                        onOpenTaskDrawer={openTaskDrawer}
                        onNavigate={handleInboxNavigate}
                        onSelectProject={setActiveProject}
                    />
                )}

                {/* All other sidebar nav items — coming soon placeholder */}
                {activeNav !== 'overview' && activeNav !== 'milestones' && activeNav !== 'dashboard' && activeNav !== 'files' && activeNav !== 'inbox' && (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="text-center space-y-3">
                            <ToolOutlined style={{ fontSize: 48, color: '#c2c6d2' }} />
                            <p className="text-base font-semibold text-[#424750] dark:text-slate-400">
                                {activeNav.charAt(0).toUpperCase() + activeNav.slice(1)} coming in a future phase.
                            </p>
                        </div>
                    </div>
                )}
            </main>

            {/* ── Modals ── */}
            <CreateProjectModal
                open={isCreateProjectOpen}
                projectToEdit={projectToEdit}
                onClose={() => { setIsCreateProjectOpen(false); setProjectToEdit(null); }}
                onSuccess={handleProjectCreated}
            />

            <TaskDetailDrawer
                mode={drawerMode}
                taskId={drawerTaskId}
                itemType={drawerItemType}
                open={isDrawerOpen}
                onClose={closeTaskDrawer}
                onSuccess={handleTaskCreatedAndOpen}
                selectedProject={drawerSelectedProject}
                selectedAssignee={filters.assignee}
                defaultStatus={drawerDefaultStatus}
                currentAdminId={currentAdmin?._id}
                currentAdmin={currentAdmin}
                onTaskUpdated={handleTaskUpdated}
                employees={employees}
                projects={projects}
            />

            <MilestoneCompletionModal
                open={isCompletionModalOpen}
                onClose={() => { setIsCompletionModalOpen(false); setCompletingMilestone(null); }}
                onConfirm={handleConfirmMilestoneCompletion}
                loading={completionLoading}
                milestone={completingMilestone || {}}
            />
        </div>
    );
}
